import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import './ccb.css';
import {
  toolbarPlugin,
  type ToolbarSlot,
  type TransformToolbarSlot,
} from '@react-pdf-viewer/toolbar';

const url = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { TermsPage } from '@src/shared/ui';
import { useProposalState, useGTM } from '@src/shared/hooks';
import { GTMEvents } from '@src/config/gtm';
import { setEtapa } from '@src/shared/zustand';
import { Etapa } from '@src/shared/models/etapa';

interface CCBProps {
  adoptLayoutExternalPublic?: boolean;
}

export const CCBPage: React.FC<CCBProps> = ({ adoptLayoutExternalPublic }) => {
  const headerRef = window.document.getElementById('header');
  const footerRef = window.document.getElementById('footer');
  const [boxHeight, setBoxHeight] = React.useState<number>(0);
  const { proposal } = useProposalState();
  const zoomPluginInstance = zoomPlugin();
  const { trackEvent } = useGTM();
  const navigate = useNavigate();
  const location = useLocation();

  const PRIVATE_CCB_ROUTE = '/terms/ccb';
  const PUBLIC_CCB_ROUTE = '/ccb';

  useEffect(() => {
    setEtapa(Etapa.CCB);
    trackEvent(GTMEvents.VIEW_CCB, {
      proposal_id: proposal?.proposalId,
    });
  }, []);

  useEffect(() => {
    const isTermsRoute = location.pathname === PRIVATE_CCB_ROUTE;
    if (isTermsRoute && !proposal?.proposalId) {
      navigate(PUBLIC_CCB_ROUTE, { replace: true });
    }
  }, [location.pathname, proposal, navigate]);

  React.useEffect(() => {
    const updateBoxHeight = () => {
      const headerHeight = headerRef?.offsetHeight || 0;
      const footerHeight = footerRef?.offsetHeight || 0;
      const totalHeight = window.innerHeight;
      if (boxHeight === 0)
        setBoxHeight(totalHeight - headerHeight - footerHeight);
    };

    updateBoxHeight();
    window.addEventListener('resize', updateBoxHeight);

    return () => {
      window.removeEventListener('resize', updateBoxHeight);
    };
  }, [footerRef, headerRef, boxHeight]);

  const toolbarPluginInstance = toolbarPlugin();
  const { renderDefaultToolbar } = toolbarPluginInstance;

  const transform: TransformToolbarSlot = (slot: ToolbarSlot) => ({
    ...slot,
    Open: () => <></>,
    OpenMenuItem: () => <></>,
    EnterFullScreen: () => <></>,
    EnterFullScreenMenuItem: () => <></>,
  });

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => (
      <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>
    ),
    sidebarTabs: (defaultTabs) => {
      return [...defaultTabs.filter((tab) => tab.title !== 'Attachment')];
    },
  });

  return (
    <TermsPage
      hiddenTitle
      hiddenBreadcrumb
      headerID='header'
      footerID='footer'
      continueButton={!adoptLayoutExternalPublic}
      adoptLayoutExternalPublic={adoptLayoutExternalPublic}
    >
      {headerRef?.offsetHeight && headerRef?.offsetHeight > 0 && (
        <div
          className='hide-scrollbar'
          style={{
            height: boxHeight,
            display: 'flex',
          }}
        >
          <div className='flex-1 bg-brand-blue-midnight-30'>
            <Worker workerUrl={url}>
              <Viewer
                theme='dark'
                fileUrl='/ccb.pdf'
                plugins={[defaultLayoutPluginInstance, zoomPluginInstance]}
                onPageChange={(page) => {
                  if (page.currentPage === 12) {
                    footerRef.focus();
                  }
                }}
              />
            </Worker>
          </div>
        </div>
      )}
    </TermsPage>
  );
};
